import { Flex, Loader, Square } from '@grubhubprod/gh-cookbook-react/dist';
import { get } from '@grubhubprod/umami-library/dist/nodash/get';
import React, { ReactNode, useState, useRef, useEffect } from 'react';
import throttle from 'lodash.throttle';
import { useScreenSizes } from 'src/utilities/useScreenSizes';

export interface IOrderHistoryTableProps {
  hideHeader?: boolean;
  hideOutsideBorder?: boolean;
  showFooter: boolean;
  headerInsets?: 'x-sm' | 'sm' | 'md' | 'lg' | 'none';
  bodyMinHeight?: string;
  bodyMaxHeight?: string;
  footerBg?: string;
  bodyBg?: string;
  headerBg?: string;
  header?: () => ReactNode;
  body?: () => ReactNode;
  footer?: () => ReactNode;
  justifyFooter?: string;
  alignFooter?: string;
  footerMinHeight?: string;
  isLoading?: boolean;
  isSignupOrSetupTable?: boolean;
  isOverflowVisible?: boolean;
}

const FOOTER_MIN_HEIGHT = '72px';

export default function Table({
  showFooter,
  hideHeader,
  hideOutsideBorder = false,
  headerInsets = 'md',
  bodyMinHeight,
  bodyMaxHeight,
  bodyBg = 'untintedBackground',
  footerBg = 'untintedBackground',
  headerBg = 'untintedBackground',
  header,
  body,
  footer,
  justifyFooter = 'center',
  alignFooter = 'center',
  footerMinHeight = FOOTER_MIN_HEIGHT,
  isLoading,
  isSignupOrSetupTable,
  isOverflowVisible,
}: IOrderHistoryTableProps): JSX.Element {
  const { isDesktop } = useScreenSizes();

  const bodyContainer = useRef<HTMLDivElement>(null);

  const [scrollShadowState, setScrollShadowState] = useState({
    showHeaderScrollShadow: false,
    showFooterScrollShadow: false,
  });

  function handleBodyScroll(event: Event): void {
    const { scrollTop, clientHeight, scrollHeight } = (get(event, 'target') ||
      {}) as HTMLDivElement;

    const isScrollable = scrollHeight > clientHeight;
    let isAtTop = true;
    let isAtBottom = false;

    if (isScrollable) {
      // use the ceiling of the scrollTop to account for scaled screens
      const scrollTopCeil = Math.ceil(scrollTop);

      // check for less than or equal to account for using the ceiling of scrollTop due to scaled screens
      isAtBottom = scrollHeight - scrollTopCeil <= clientHeight;
      isAtTop = scrollTop === 0;
    }

    setScrollShadowState({
      showHeaderScrollShadow: isScrollable && !isAtTop,
      showFooterScrollShadow: isScrollable && !isAtBottom,
    });
  }

  const throttledScrollHandler = throttle(handleBodyScroll, 200, {
    trailing: true,
  });

  useEffect(() => {
    if (bodyContainer.current) {
      bodyContainer.current.addEventListener('scroll', throttledScrollHandler);
    }

    return () => {
      if (bodyContainer.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        bodyContainer.current.removeEventListener(
          'scroll',
          throttledScrollHandler
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyContainer]);

  const dimensionValue = hideOutsideBorder ? undefined : '1';

  let headerInset = {};

  if (headerInsets !== 'none') {
    headerInset = {
      inset: headerInsets,
    };
  }
  return (
    <Square dimension={dimensionValue} borderRadius="4px">
      {!hideHeader && (
        <Square
          bg={headerBg}
          data-testid="table-header"
          borderBottom="1px solid"
          borderColor="lightLine"
          dimension={scrollShadowState.showHeaderScrollShadow ? '2' : null}
          {...headerInset}
          borderTopLeftRadius="4px"
          borderTopRightRadius="4px"
          style={{ position: 'relative' }}
          zIndex={1} // stay on top of the body container (TODO: make variable?)
        >
          {header && header()}
        </Square>
      )}

      <Square
        position="relative"
        id="table-body"
        bg={bodyBg}
        data-testid="table-body"
        maxHeight={bodyMaxHeight}
        minHeight={bodyMinHeight}
        ref={bodyContainer}
        borderTopLeftRadius={!isDesktop ? '4px' : '0'}
        borderTopRightRadius={!isDesktop ? '4px' : '0'}
        style={{
          overflowY:
            (isDesktop || isSignupOrSetupTable) && !isOverflowVisible
              ? 'auto'
              : 'visible',
          opacity: isLoading ? 0.5 : 1,
        }}
      >
        {isLoading && (
          <Flex
            alignItems="center"
            justifyContent="center"
            position="absolute"
            width="100%"
            height="100%"
            zIndex={10}
          >
            <Loader size="md" />
          </Flex>
        )}
        {body && body()}
      </Square>

      {showFooter && (
        <Flex
          data-testid="table-footer"
          bg={footerBg}
          alignItems={alignFooter}
          justifyContent={justifyFooter}
          minHeight={footerMinHeight}
          borderBottomLeftRadius="4px"
          borderBottomRightRadius="4px"
          borderTop="1px solid"
          borderColor="lightLine"
          dimension={scrollShadowState.showFooterScrollShadow ? '3' : '2'}
          style={{ position: 'relative' }}
        >
          {footer && footer()}
        </Flex>
      )}
    </Square>
  );
}
